import {
	CardHeaderActions,
	CheckboxField,
	DrawerBody,
	DrawerFooter,
	DrawerHeader,
	DrawerHeading,
	EntityDrawerProps,
	FCC,
	FormDrawer,
	IconLinkButton,
	Id,
	SubmitButton,
	TextField,
} from "@dgs/core";
import { useField } from "formik";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Address } from "../../../../shared/ui/Address";
import { CreatableCompanySelect } from "../../companies/CreatableCompanySelect";
import { IContactCompanyFormState, IContactCompanyUpdateResource } from "../../shared/types";

const Wrapper = styled.div`
	position: absolute;
	width: 100%;
	border-top: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	margin-top: ${({ theme }) => theme.spacing(14)};
	margin-left: -${({ theme }) => theme.spacing(4)};
	padding-top: ${({ theme }) => theme.spacing(2)};
	padding-left: ${({ theme }) => theme.spacing(4)};
`;

const Bold = styled.span`
	font-weight: bold;
	line-height: 2;
`;

interface Props extends EntityDrawerProps<IContactCompanyFormState> {
	disableCompanySelect?: boolean;
	companyId?: Id;
}

export const ContactCompanyDrawer: FCC<Props> = ({ disableCompanySelect = false, companyId, ...drawerProps }) => {
	const { t } = useTranslation();

	const validate = useCallback(
		(values: IContactCompanyFormState) => {
			const errors: { [K in keyof IContactCompanyFormState]?: any } = {};

			if (!values.company) {
				errors.company = t("Required field");
			}

			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			{...drawerProps}
			validate={validate}
			renderForm={({ onClose, title }) => (
				<>
					<DrawerHeader onClose={onClose}>
						<DrawerHeading>{title}</DrawerHeading>
						{companyId && (
							<CardHeaderActions>
								<IconLinkButton icon="eye" to={`/companies/${companyId}`} title={t("View company")} type="button" />
							</CardHeaderActions>
						)}
					</DrawerHeader>
					<DrawerBody>
						<CreatableCompanySelect name="company" label={t("Company")} disabled={disableCompanySelect} required />
						<TextField name="position" label={t("Position")} />
						<CheckboxField label={t("Event contact")} name="isEvent" />
						<CheckboxField label={t("Car fleet contact")} name="isCarFleet" />
						{drawerProps.children}
					</DrawerBody>
					<DrawerFooter>
						<Footer />
						<SubmitButton>{t("Save")}</SubmitButton>
					</DrawerFooter>
				</>
			)}
		/>
	);
};

const Footer = () => {
	const { t } = useTranslation();
	const [{ value }] = useField<IContactCompanyUpdateResource | null>("company");

	if (!value || !value.primaryAddress) {
		return null;
	}

	return (
		<Wrapper>
			<Bold>{t("Primary company address")}</Bold>
			<Address address={value.primaryAddress} />
		</Wrapper>
	);
};
